import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../css/UnitList.css"; // Assuming the same CSS can be used

const DepartmentList = () => {
  const navigate = useNavigate();
  const [departments, setDepartments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [deleteModal, setDeleteModal] = useState({ show: false, department: null });
  const API_URL = process.env.REACT_APP_API_URL || "";

  useEffect(() => {
    const apiUrl = `${API_URL}department-list.php`;
    fetch(apiUrl)
      .then((res) => res.json())
      .then((data) => {
        // Changed 'data.departments' to 'data.data' to match your JSON response
        if (Array.isArray(data.data)) {
          setDepartments(data.data);
        } else {
          showToast("Failed to load departments", "error");
        }
      })
      .catch(() => showToast("Error fetching departments", "error"));
  }, [API_URL]);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 3000);
  };

  const filteredDepartments = departments.filter((department) => {
    // Changed 'department.departmentname' to 'department.dept_name'
    const matchesSearch = department.dept_name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const handleEdit = (department) => {
    navigate("/add-department", { state: { department, isEditing: true } });
    showToast(`Editing department: ${department.dept_name}`, "info");
  };

  const handleDelete = (department) => {
    setDeleteModal({ show: true, department });
  };

  const confirmDelete = async () => {
    const { department } = deleteModal;
    try {
      const response = await fetch(`${API_URL}department-delete.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id: department.id }),
      });

      const data = await response.json();

      if (data.message && data.message.includes("deleted successfully")) {
        showToast(
          `Department "${department.dept_name}" deleted successfully`,
          "success"
        );
        setDepartments((prevDepartments) =>
          prevDepartments.filter((dept) => dept.id !== department.id)
        );
      } else if (data.status === "success") {
        // Fallback for APIs that use status field
        showToast(
          `Department "${department.dept_name}" deleted successfully`,
          "success"
        );
        setDepartments((prevDepartments) =>
          prevDepartments.filter((dept) => dept.id !== department.id)
        );
      } else {
        showToast(data.message || "Failed to delete department", "error");
      }
    } catch (error) {
      showToast("Network error. Could not delete department.", "error");
    }
    setDeleteModal({ show: false, department: null });
  };

  const cancelDelete = () => {
    setDeleteModal({ show: false, department: null });
  };

  const handleAddDepartment = () => {
    navigate("/add-department");
    showToast("Navigating to add new department", "info");
  };

  return (
    <div className="category-list-content">
      <div className="page-header">
        <div className="header-content">
          <h1>Department Management</h1>
          <p>Manage employee departments</p>
        </div>
        <button className="add-unit-button" onClick={handleAddDepartment}>
          + Add New Department
        </button>
      </div>

      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search departments by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="unit-list-table-container">
        <table className="unit-list-table">
          <thead>
            <tr>
              <th>Department Name</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredDepartments.map((department) => (
              <tr key={department.id}>
                <td>
                  <div className="unit-info">
                    <div className="unit-icon">🏢</div>

                    <span>{department.dept_name}</span>
                  </div>
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="action-btn edit"
                      onClick={() => handleEdit(department)}
                    >
                      Edit
                    </button>
                    <button
                      className="action-btn delete"
                      onClick={() => handleDelete(department)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="table-footer">
        <div className="results-info">
          Showing {filteredDepartments.length} of {departments.length}{" "}
          departments
        </div>
      </div>

      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
            <button
              className="toast-close"
              onClick={() => setToast({ show: false, message: "", type: "" })}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Confirm Delete</h3>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to delete department{" "}
                <strong>"{deleteModal.department?.dept_name}"</strong>?
              </p>
              <p>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={cancelDelete}>
                No, Cancel
              </button>
              <button className="btn btn-danger" onClick={confirmDelete}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepartmentList;
